/**
 * Capability Helpers and Conversion Functions
 * 
 * Converts roles to permission objects for easy consumption in components
 */

import type { Role, Capability, Permissions } from '@/lib/types'
import { ROLE_CAPABILITIES } from './roles'

/**
 * All capabilities (for creating default false object)
 */
const ALL_CAPABILITIES: Capability[] = [
  'canView',
  'canEditEntries',
  'canDeleteEntries',
  'canCreateRecurrences',
  'canEditRecurrences',
  'canTerminateRecurrences',
  'canDeleteRecurrences',
  'canManageTeam',
  'canManageSettings',
  'canAccessBilling',
  'canTransferOwnership',
  'canDeleteUnit',
]

/**
 * Convert a role to a permissions object
 * Useful for checking capabilities in components: if (permissions.canDeleteEntries) { ... }
 * 
 * @param role - The user's role, or null/undefined for no access
 * @returns Permissions object with all capabilities set
 */
export function roleToPermissions(role: Role | null | undefined): Permissions {
  if (!role) {
    return createDefaultPermissions()
  }

  const roleCapabilities = ROLE_CAPABILITIES[role] ?? []

  return {
    canView: roleCapabilities.includes('canView'),
    canEditEntries: roleCapabilities.includes('canEditEntries'),
    canDeleteEntries: roleCapabilities.includes('canDeleteEntries'),
    canCreateRecurrences: roleCapabilities.includes('canCreateRecurrences'),
    canEditRecurrences: roleCapabilities.includes('canEditRecurrences'),
    canTerminateRecurrences: roleCapabilities.includes('canTerminateRecurrences'),
    canDeleteRecurrences: roleCapabilities.includes('canDeleteRecurrences'),
    canManageTeam: roleCapabilities.includes('canManageTeam'),
    canManageSettings: roleCapabilities.includes('canManageSettings'),
    canAccessBilling: roleCapabilities.includes('canAccessBilling'),
    canTransferOwnership: roleCapabilities.includes('canTransferOwnership'),
    canDeleteUnit: roleCapabilities.includes('canDeleteUnit'),
  }
}

/**
 * Check if a role has a specific capability
 * Lightweight check for quick permission validation
 * 
 * @param role - The user's role
 * @param capability - The capability to check
 * @returns true if role has capability, false otherwise
 */
export function canUserDo(role: Role | null | undefined, capability: Capability): boolean {
  if (!role) return false
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false
}

/**
 * Check if user can perform action (with owner override)
 * For cases where owner should bypass normal capabilities
 * 
 * @param role - The user's role
 * @param capability - The capability to check
 * @param isOwner - Whether user is unit owner
 * @returns true if allowed
 */
export function canUserDoOrIsOwner(
  role: Role | null | undefined,
  capability: Capability,
  isOwner: boolean = false
): boolean {
  // Owner can do everything
  if (isOwner) return true
  
  return canUserDo(role, capability)
}

/**
 * Create a default permissions object with all capabilities set to false
 */
function createDefaultPermissions(): Permissions {
  return {
    canView: false,
    canEditEntries: false,
    canDeleteEntries: false,
    canCreateRecurrences: false,
    canEditRecurrences: false,
    canTerminateRecurrences: false,
    canDeleteRecurrences: false,
    canManageTeam: false,
    canManageSettings: false,
    canAccessBilling: false,
    canTransferOwnership: false,
    canDeleteUnit: false,
  }
}

/**
 * Merge multiple permission sets (take the most permissive)
 * Useful for combining permissions from multiple roles
 * 
 * @param permissionsList - Array of permission objects
 * @returns Merged permissions (true if ANY allows)
 */
export function mergePermissions(...permissionsList: Permissions[]): Permissions {
  const merged = createDefaultPermissions()

  for (const permissions of permissionsList) {
    for (const [key, value] of Object.entries(permissions)) {
      if (value === true) {
        merged[key as Capability] = true
      }
    }
  }

  return merged
}

export { ROLE_CAPABILITIES } from './roles'
export { getRoleCapabilities, roleHasCapability } from './roles'
export { ROLE_LABELS, ROLE_DESCRIPTIONS, ALL_ROLES } from './roles'
export { ROLES_CAN_INVITE, ROLES_CAN_ACCESS_BILLING } from './roles'
